// Everything the teacher analysis page shows is computed here from the coded utterances (call map
// section 11): the API sends codes, never statistics, confidences or probabilities.

// Colours for the charts. Speakers: teacher blue, student orange (AA on white, as in codingColors).
// RC: the dataviz reference palette's hues, validated as a set (CVD and normal-vision separation
// pass; ER and OD are under 3:1 on white, so every segment carries a visible label and the table
// lists every code). O is the neutral "not applicable" grey, outside the categorical set.
export const SPEAKER_SERIES = { teacher: '#1565C0', student: '#B45309', other: '#9AA1AB' };

export const RC_ORDER = ['ER', 'SK', 'OD', 'INF', 'O'];
export const RC_COLORS = { ER: '#E87BA4', SK: '#2A78D6', OD: '#1BAF7A', INF: '#EB6834', O: '#B8BEC6' };
export const RC_NAMES = { ER: 'Everyday reasoning', SK: 'Scientific knowledge', OD: 'Observation / data', INF: 'Inference from data', O: 'Not applicable' };

export const UT_ORDER = ['Q', 'P', 'E', 'Fy', 'Fs', 'Fq', 'Ry', 'Rs', 'Rq', 'O'];
export const UT_NAMES = {
    Q: 'Question', P: 'Prompt', E: 'Example / explanation', Fy: 'Confirmatory feedback', Fs: 'Feedback with statements',
    Fq: 'Feedback with a question', Ry: 'Confirmatory response', Rs: 'Response with statements', Rq: 'Response with a question', O: 'Other / inaudible',
};
const STUDENT_UT = ['Ry', 'Rs', 'Rq'];
export const utSpeaker = (code) => (code === 'O' ? 'other' : STUDENT_UT.includes(code) ? 'student' : 'teacher');

// 5-class CCI weights (decision 2026-10-24, YES-205): O 0, ER 1, SK 2, OD 2, INF 3
export const CCI = { O: 0, ER: 1, SK: 2, OD: 2, INF: 3 };

const mean = (values) => (values.length ? values.reduce((a, b) => a + b, 0) / values.length : 0);
const round1 = (n) => Math.round(n * 10) / 10;

export const summarize = (utterances) => {
    const teacher = utterances.filter((u) => u.role === 'teacher');
    const student = utterances.filter((u) => u.role === 'student');
    const count = (code) => utterances.filter((u) => u.ut === code).length;
    const questions = count('Q');
    const cci = (list) => round1(mean(list.map((u) => CCI[u.rc] ?? 0)));

    const ut = UT_ORDER.map((code) => ({ code, name: UT_NAMES[code], count: count(code), speaker: utSpeaker(code) }));
    const rcBy = (list) => Object.fromEntries(RC_ORDER.map((code) => [code, list.length ? (list.filter((u) => u.rc === code).length / list.length) * 100 : 0]));

    // 10-utterance rolling average of CCI, for the timeline
    const timeline = utterances.map((u, i) => {
        const window = utterances.slice(Math.max(0, i - 9), i + 1).map((x) => CCI[x.rc] ?? 0);
        return { index: i + 1, number: u.number, role: u.role, cci: CCI[u.rc] ?? 0, rolling: round1(mean(window)), rc: u.rc, text: u.text };
    });

    return {
        total: utterances.length,
        teacher: teacher.length,
        student: student.length,
        teacherShare: utterances.length ? Math.round((teacher.length / utterances.length) * 100) : 0,
        // Today's Interaction Ratio, renamed for teachers: student Rs + Ry over teacher Q
        responsesPerQuestion: questions ? round1((count('Rs') + count('Ry')) / questions) : null,
        cci: { lesson: cci(utterances), teacher: cci(teacher), student: cci(student) },
        ut,
        rc: [
            { speaker: 'Teacher', role: 'teacher', count: teacher.length, ...rcBy(teacher) },
            { speaker: 'Student', role: 'student', count: student.length, ...rcBy(student) },
        ],
        timeline,
    };
};
